import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { Demographics } from 'entities';

import { Checkbox, Column, Label, LabeledInput, Row } from 'components/Blocks';
import { TextToggle } from 'components/Inputs';

import { demographicsRenderer, defaultDemographics } from './utils';

interface DemographicsFormProps {
  demographics: Demographics;
  handleChange: (name: string, value: string | boolean) => void;
}

export { defaultDemographics, demographicsRenderer };

export function DemographicsForm({
  demographics,
  handleChange,
}: DemographicsFormProps): JSX.Element {
  const { t } = useLang();

  const handleInputChange = useCallback(
    ({ target }: { target: { name: string; value: string } }) => {
      const { name, value } = target;

      handleChange(name, value);
    },
    [handleChange],
  );

  const handleNACheck = useCallback(
    ({ target }: { target: { name?: string; checked: boolean } }) => {
      const { name, checked } = target;

      handleChange(name!, checked ? 'N/A' : '');
    },
    [handleChange],
  );

  const setQuotas = (quotaDetails: string): void => {
    handleChange('quotaDetails', quotaDetails);
  };

  const toggleQuota = (): void => {
    handleChange('quota', !demographics.quota);
    handleChange('quotaDetails', '');
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4 small_tablet:grid-cols-1">
        {demographicsRenderer.map(({ key, getLabel, Icon }) => (
          <Column gap="2" key={`demographics-${key}`}>
            <Label>
              <Icon size={14} />
              {getLabel(t)}
            </Label>

            <Row gap="2" relative>
              <LabeledInput
                name={key}
                value={(demographics[key] as string) || ''}
                onChange={handleInputChange}
                fullWidth
                className="pr-20"
                disabled={demographics[key] === 'N/A'}
              />

              <Row className="absolute bottom-2 right-2.5" gap="2">
                <Checkbox
                  name={key}
                  checked={demographics[key] === 'N/A'}
                  size="small"
                  onChange={handleNACheck}
                />
                <Label className="text-sm">N/A</Label>
              </Row>
            </Row>
          </Column>
        ))}
      </div>

      <TextToggle
        isActive={!!demographics.quota}
        toggle={toggleQuota}
        text={demographics.quotaDetails || ''}
        setText={setQuotas}
        label={t('quotas')}
        description={
          t('describe-how-its-going-to-work-if-a-file-is-needed-reference-below-and-upload-it')!
        }
      />
    </>
  );
}
